import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import styles from "./AdmissionCaptureIntList.module.css";
import { CustomButton } from "../../../components";
import CustomMRTTable from "../../../components/CustomMRTTable/CustomMRTTable";
import PopupModal from "../../../components/PopupModal/PopupModal";
import AxiosInstance, { TokenService } from "../../../services/Api";

const ViewAdmissionCaptureIntList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = TokenService.getUser();

  const clientData = user?.client_data[0] || {};
  const school_id = clientData?.school_id || "";
  const { translations } = useSelector((state) => state.language);
  const t = useMemo(() => translations?.labels || {}, [translations]);
  const { dataRows } = location.state || {};

  // ─── State ───
  const [viewData, setViewData] = useState({});
  const [sectionList, setSectionList] = useState([]);
  const [selectedSections, setSelectedSections] = useState({});
  const [resultStatusMap, setResultStatusMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    message: "",
    redirectPath: "",
  });
  const [columnFilters, setColumnFilters] = useState([]);

  // ─── Fetch view data ───
  const fetchViewData = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {};
      columnFilters.forEach((filter) => {
        filters[filter.id] = filter.value;
      });

      const payload = {
        school_id: school_id,
        class_id: dataRows?.class_id,
        gender: filters.gender || "",
        student_id: filters.student_id || "",
        student_name: filters.student_name || "",
        section: filters.section_id || "",
        subject_marks: filters.subject_marks || "",
        total_marks: filters.overall_marks || "",
        result: filters.result || "",
        result_status: filters.result_status || "",
        search: apiSearch || "",
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
      };

      const response = await AxiosInstance.post(
        "/admission/annualAcadmic/resultView",
        payload,
      );

      if (response?.data?.status) {
        setViewData(response.data.data || {});
        setRowCount(response.data.pagination?.total || 0);
      } else {
        setViewData({});
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching result view:", error);
      setViewData({});
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    school_id,
    dataRows?.class_id,
    pagination.pageIndex,
    pagination.pageSize,
    columnFilters,
    apiSearch,
  ]);

  useEffect(() => {
    if (school_id && dataRows?.class_id) {
      fetchViewData();
    }
  }, [fetchViewData]);

  // ─── Fetch section dropdown ───
  const fetchSectionList = useCallback(async () => {
    try {
      const response = await AxiosInstance.post(
        "/school/timeTable/sectionDropdown",
        {
          id: dataRows?.class_id || "",
          school_id: school_id,
        },
      );

      if (response?.data?.status) {
        const apiData = response.data.data || [];
        const transformedData = apiData.map((item) => ({
          id: item.id,
          class_id: item.class_id,
          section: item.section_id,
          label: item.section_id,
          value: item.section_id,
        }));
        setSectionList(transformedData);
      } else {
        setSectionList([]);
      }
    } catch (error) {
      console.error("Error fetching section list:", error);
      setSectionList([]);
    }
  }, [school_id, dataRows?.class_id]);

  useEffect(() => {
    if (school_id && dataRows?.class_id) {
      fetchSectionList();
    }
  }, [fetchSectionList]);

  // ─── Initialize result status & sections from API data ───
  const summary = viewData?.summary || {};
  const students = viewData?.students || [];
  const subjects = useMemo(() => {
    if (!students.length) return [];
    return students[0]?.subject_details || [];
  }, [students]);

  const className = viewData?.class_name || "";
  const sectionName = viewData?.section || "";
  const examType = viewData?.exam_type || "";
  const startDate = viewData?.start_date
    ? moment(viewData.start_date).format("DD/MM/YYYY")
    : "";
  const endDate = viewData?.end_date
    ? moment(viewData.end_date).format("DD/MM/YYYY")
    : "";
  const transitionStatus = viewData?.isPublish || 0;

  useEffect(() => {
    if (!students.length) return;

    const initialStatus = {};
    const initialSections = {};

    students.forEach((student) => {
      if (student.result_status === "Promoted") {
        initialStatus[student.id] = "1";
      } else if (student.result_status === "Detained") {
        initialStatus[student.id] = "0";
      } else if (student.result_status === "Transferred") {
        initialStatus[student.id] = "Transferred";
      }
      initialSections[student.student_id] = student.section_id;
    });

    setResultStatusMap(initialStatus);
    setSelectedSections(initialSections);
  }, [students]);

  // ─── Result status options ───
  const resultStatusOptions = useMemo(
    () => [
      { label: t.detained || "Detained", value: "0" },
      { label: t.promoted || "Promoted", value: "1" },
      // { label: t.transferred || "Transferred", value: "2" },
    ],
    [t],
  );

  // ─── Handlers ───
  const handleResultChange = useCallback(
    async (studentId, value) => {
      const selectedOption = resultStatusOptions.find(
        (item) => item.value === value,
      );
      const statusLabel = selectedOption?.label || "";

      setResultStatusMap((prev) => ({ ...prev, [studentId]: value }));

      try {
        const payload = {
          id: studentId,
          status: statusLabel,
          updated_by: user.id,
        };

        await AxiosInstance.post(
          "/admission/annualAcadmic/studentPromotionUpdate",
          payload,
        );
      } catch (error) {
        console.error("Error updating result status:", error);
      }
    },
    [resultStatusOptions, user?.id],
  );

  const handleSectionChange = useCallback((studentId, section) => {
    setSelectedSections((prev) => ({ ...prev, [studentId]: section }));
  }, []);

  // ─── Publish ───
  const handleAnnualPublish = useCallback(async () => {
    // ── Validate: all students must have a result status selected ──
    const unselectedStudents = students.filter(
      (student) =>
        !resultStatusMap[student.id] &&
        resultStatusMap[student.id] !== "Transferred",
    );
    if (unselectedStudents.length > 0) {
      setModalState({
        open: true,
        type: "error",
        message:
          t.selectResultStatusForAll ||
          `Please select result status for all students. ${unselectedStudents.length} student(s) remaining.`,
        redirectPath: "",
      });
      return;
    }

    setPublishLoading(true);
    try {
      const student_details = students.map((student, index) => {
        const statusValue = resultStatusMap[student.id];
        const statusLabel =
          statusValue === "1"
            ? "Promoted"
            : statusValue === "0"
              ? "Detained"
              : statusValue === "Transferred"
                ? "Transferred"
                : "";

        return {
          id: index + 1,
          status: statusLabel,
          student_id: student.student_id,
          class_id:
            statusValue === "1"
              ? Number(dataRows?.class_id) + 1
              : Number(dataRows?.class_id),
          section: selectedSections[student.student_id] || student.section_id,
        };
      });

      const payload = {
        class_id: dataRows?.class_id,
        school_id: school_id,
        student_details: student_details,
        created_by: user.id,
      };

      const response = await AxiosInstance.post(
        "/admission/annualAcadmic/publishAnnual",
        payload,
      );

      if (response?.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message:
            response.data?.message ||
            t.publishSuccess ||
            "Student promotion processed successfully",
          redirectPath: "/user/Academic-Transition",
        });
        fetchViewData();
      } else {
        setModalState({
          open: true,
          type: "error",
          message:
            response.data?.message ||
            t.publishFailed ||
            "Failed to publish result.",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error publishing result:", error);
      setModalState({
        open: true,
        type: "error",
        message: t.publishFailed || "Failed to publish result.",
        redirectPath: "",
      });
    } finally {
      setPublishLoading(false);
    }
  }, [
    students,
    resultStatusMap,
    selectedSections,
    dataRows?.class_id,
    school_id,
    user?.id,
    t,
    fetchViewData,
  ]);

  const handleCancel = () => {
    navigate("/user/Academic-Transition");
  };

  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({ ...prev, open: false }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };

  // ─── Table data ───
  const tableData = useMemo(() => {
    return students.map((student, index) => {
      const row = {
        sno: pagination.pageIndex * pagination.pageSize + index + 1,
        id: student.id,
        student_id: student.student_id,
        student_name: student.student_name,
        gender: student.gender
          ? student.gender.charAt(0).toUpperCase() +
            student.gender.slice(1).toLowerCase()
          : "",
        class_name: student.class_name || className,
        section_id: student.section_id,
        overall_marks: student.overall_marks,
        result: student.result,
        rank: student.rank,
      };

      student.subject_details?.forEach((sub) => {
        row[`subject_${sub.subject_id}`] = sub.total;
      });

      return row;
    });
  }, [students, pagination.pageIndex, pagination.pageSize, className]);

  // ─── Columns ───
  const columns = useMemo(() => {
    const baseCols = [
      {
        accessorKey: "sno",
        header: t.serialNo || "S.No.",
        size: 70,
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "student_id",
        header: t.studentId || "Student ID",
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: "student_name",
        header: t.studentName || "Student Name",
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: "gender",
        header: t.gender || "Gender",
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: "class_name",
        header: t.class || "Class",
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: "section_id",
        header: t.section || "Section",
        enableSorting: true,
        enableColumnFilter: true,
      },
    ];

    const subjectCols = subjects.map((sub) => ({
      accessorKey: `subject_${sub.subject_id}`,
      header: sub.subject_name,
      enableSorting: true,
      enableColumnFilter: true,
      size: 110,
    }));

    const endCols = [
      {
        accessorKey: "overall_marks",
        header: t.totalMark || "Total Mark",
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: "rank",
        header: t.rank || "Rank",
        enableSorting: true,
        enableColumnFilter: false,
        size: 80,
      },
      {
        accessorKey: "result",
        header: t.passFail || "Pass / Fail",
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: "result_status",
        header: t.annualTransitionStatus || "Result",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const studentId = row.original.id;
          const isDisabled = transitionStatus === 1;
          const currentValue = String(resultStatusMap[studentId] ?? "");
          const className = row.original.class_name;

          if (
            currentValue === "Transferred" &&
            (className === "X" || className === "XII")
          ) {
            return <span className="">Transferred</span>;
          }

          return (
            <select
              className="form-select"
              value={currentValue}
              onChange={(e) => handleResultChange(studentId, e.target.value)}
              disabled={isDisabled}
              style={isDisabled ? { opacity: 0.6, cursor: "not-allowed" } : {}}
            >
              <option value="">{t.select || "Select"}</option>
              {resultStatusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          );
        },
      },
      {
        accessorKey: "assign_section",
        header: t.assignSection || "Assign Section",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const studentId = row.original.student_id;
          const isDisabled = transitionStatus === 1;

          return (
            <select
              className="form-select"
              value={selectedSections[studentId] || ""}
              onChange={(e) => handleSectionChange(studentId, e.target.value)}
              disabled={isDisabled}
              style={isDisabled ? { opacity: 0.6, cursor: "not-allowed" } : {}}
            >
              <option value="">{t.selectSection || "Select Section"}</option>
              {sectionList?.map((option) => (
                <option key={option.id} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          );
        },
      },
    ];

    return [...baseCols, ...subjectCols, ...endCols];
  }, [
    subjects,
    sectionList,
    selectedSections,
    resultStatusMap,
    resultStatusOptions,
    transitionStatus,
    t,
    handleResultChange,
    handleSectionChange,
  ]);

  const columnOrder = useMemo(() => {
    const subjectKeys = subjects.map((sub) => `subject_${sub.subject_id}`);
    return [
      "sno",
      "student_id",
      "student_name",
      "gender",
      "class_name",
      "section_id",
      ...subjectKeys,
      "overall_marks",
      "rank",
      "result",
      "result_status",
      "assign_section",
    ];
  }, [subjects]);

  return (
    <>
      <PopupModal
        open={modalState.open}
        title={
          modalState.type === "success"
            ? t.success || "Success"
            : t.failed || "Failed"
        }
        message={modalState.message}
        onOk={handleSuccessOk}
      />

      <div className={styles.container}>
        {/* ─── Top Header Bar ─── */}
        <div className={styles.topBar}>
          <div className={styles.topBarLeft}>
            <h1 className={styles.examTitle}>{examType || "Annual Exam"}</h1>
          </div>
          <div className={styles.topBarRight}>
            <span className={styles.topBarInfo}>
              {t.classLabel || "Class"}: {className || "-"}
            </span>
            <span className={styles.topBarDivider}>|</span>
            <span className={styles.topBarInfo}>
              {t.section || "Section"}: {sectionName || "-"}
            </span>
            <span className={styles.topBarDivider}>|</span>
            <span className={styles.topBarInfo}>
              {t.dates || "Dates"}: {startDate || "-"} - {endDate || "-"}
            </span>
            <span className={styles.topBarDivider}>|</span>
            <span className={styles.topBarInfo}>
              {t.annualTransitionStatus || "Annual Transition Status"}:{" "}
              {transitionStatus === 1
                ? t.TransitionCompleted || "Transition Completed"
                : t.TransitionNotCompleted || "Transition Not Completed"}
            </span>
          </div>
        </div>

        {/* ─── Summary Cards ─── */}
        <div className={styles.summaryRowView}>
          <div className={styles.summaryCard}>
            <p className={styles.summaryLabel}>
              {t.totalStudents || "Total Students"}
            </p>
            <h2 className={styles.summaryValue}>
              {summary.total_students || 0}
            </h2>
            <div className={styles.summaryGender}>
              <span className={styles.genderBoys}>
                {summary.total_gender?.boys || 0}
              </span>
              <span className={styles.genderGirls}>
                {summary.total_gender?.girls || 0}
              </span>
            </div>
          </div>

          <div className={styles.summaryCard}>
            <p className={styles.summaryLabel}>
              {t.studentsPassed || "Students Passed"}
            </p>
            <h2 className={styles.summaryValue}>
              {summary.students_passed || 0}
            </h2>
            <div className={styles.summaryGender}>
              <span className={styles.genderBoys}>
                {summary.gender_passed?.boys || 0}
              </span>
              <span className={styles.genderGirls}>
                {summary.gender_passed?.girls || 0}
              </span>
            </div>
          </div>

          <div className={styles.summaryCard}>
            <p className={styles.summaryLabel}>
              {t.passedPercentage || "Passed Percentage"}
            </p>
            <h2 className={styles.summaryValue}>
              {summary.pass_percentage ?? 0}%
            </h2>
            <div className={styles.summaryGender}>
              <span className={styles.genderBoys}>
                {summary.gender_pass_percentage?.boys ?? 0}%
              </span>
              <span className={styles.genderGirls}>
                {summary.gender_pass_percentage?.girls ?? 0}%
              </span>
            </div>
          </div>

          <div className={styles.summaryCard}>
            <p className={styles.summaryLabel}>
              {t.totalPromoted || "Total Promoted"}
            </p>
            <h2 className={styles.summaryValue}>
              {summary.students_promoted ?? 0}
            </h2>
            <div className={styles.summaryGender}>
              <span className={styles.genderBoys}>
                {summary.gender_promoted?.boys ?? 0}
              </span>
              <span className={styles.genderGirls}>
                {summary.gender_promoted?.girls ?? 0}
              </span>
            </div>
          </div>

          <div className={styles.summaryCard}>
            <p className={styles.summaryLabel}>
              {t.studentsFailed || "Students Failed"}
            </p>
            <h2 className={styles.summaryValue}>
              {summary.students_failed || 0}
            </h2>
            <div className={styles.summaryGender}>
              <span className={styles.genderBoys}>
                {summary.gender_failed?.boys || 0}
              </span>
              <span className={styles.genderGirls}>
                {summary.gender_failed?.girls || 0}
              </span>
            </div>
          </div>

          <div className={styles.summaryCard}>
            <p className={styles.summaryLabel}>
              {t.failedPercentage || "Failed Percentage"}
            </p>
            <h2 className={styles.summaryValue}>
              {summary.fail_percentage ?? 0}%
            </h2>
            <div className={styles.summaryGender}>
              <span className={styles.genderBoys}>
                {summary.gender_fail_percentage?.boys ?? 0}%
              </span>
              <span className={styles.genderGirls}>
                {summary.gender_fail_percentage?.girls ?? 0}%
              </span>
            </div>
          </div>

          <div className={styles.summaryCard}>
            <p className={styles.summaryLabel}>
              {t.totalDetained || "Total Detained"}
            </p>
            <h2 className={styles.summaryValue}>
              {summary.students_failed ?? 0}
            </h2>
            <div className={styles.summaryGender}>
              <span className={styles.genderBoys}>
                {summary.gender_failed?.boys ?? 0}
              </span>
              <span className={styles.genderGirls}>
                {summary.gender_failed?.girls ?? 0}
              </span>
            </div>
          </div>
        </div>

        {/* ─── Table ─── */}
        <div className={styles.tableSection}>
          <CustomMRTTable
            columns={columns}
            data={tableData}
            columnOrder={columnOrder}
            loading={loading}
            enablePagination={true}
            manualPagination
            enableFiltering={true}
            enableSearch={true}
            enableSorting={true}
            columnFilters={columnFilters}
            onColumnFiltersChange={setColumnFilters}
            pagination={pagination}
            rowCount={rowCount}
            onPaginationChange={setPagination}
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            onSearchEnter={(value) => {
              setApiSearch(value);
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
          />
        </div>

        {/* ─── Buttons ─── */}
        <div className="row" style={{ justifyContent: "center" }}>
          <div className="col-lg-3 px-2 col-md-6 col-sm-12 mt-3">
            <CustomButton
              variant="contained"
              onClick={handleCancel}
              className={styles.cancelButton}
            >
              {t.cancel || "CANCEL"}
            </CustomButton>
          </div>
          <div className="col-lg-3 px-2 col-md-6 col-sm-12 mt-3">
            <CustomButton
              variant="contained"
              onClick={handleAnnualPublish}
              className={styles.saveButton}
              disabled={transitionStatus === 1 || publishLoading}
              loading={publishLoading}
              loadingText={t.submitting || "Submitting..."}
              style={{
                opacity: transitionStatus === 1 ? 0.5 : 1,
                cursor: transitionStatus === 1 ? "not-allowed" : "pointer",
              }}
            >
              {t.publishAnnualTransition || "Publish Annual Transition"}
            </CustomButton>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewAdmissionCaptureIntList;
